import React from 'react'
import { Button, Flex, H2, Input, Span, SelectDropdown, Line, Toggle } from '../../lib/components'
import { OpportunityCard } from '../../lib/components/OpportunityCard'
import { Pagination } from '../../lib/components/Pagination'
import './opportunity-list.scss'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'
import { Opportunity } from '../../lib/types'
import {
  calculateCommittedPercentage,
  displayPercentage,
  displayTicketSize,
} from '../../lib/utils/helpers'
import { projectSort } from '../../lib/utils/message'

function OpportunityListPresenter({
  opportunities,
  searchParams,
  handleSearchParams,
  resetSearchParams,
  capitalizeStatus,
  handleProjectClick,
  pagination,
  handleCurrentPage,
  currentPage,
  handleProjectInvestmentClick,
  handleProjectWithdrawalClick,
  isCloseStatusToggle,
  handleClosingFilterToggle,
  assetClasses,
  canEditOrder,
  handleDisplayOrderSave,
}: any) {
  const { breakpoint } = useBreakpoint()

  const adminFilters = [
    ...assetClasses,
    { text: 'Published opportunity', value: 'PUBLISHED' },
    { text: 'Unpublished opportunity', value: 'UNPUBLISHED' },
  ]

  const selectedFilterValue = searchParams.status || searchParams.filterBy || ''

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-48']} text='Browse Opportunities' />
      <div className='toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword}
          handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
          placeholder='Search'
        />
        <SelectDropdown
          name='filter'
          id='filterId'
          formId=''
          list={adminFilters}
          value={selectedFilterValue}
          placeholder={'Filter'}
          handleChange={(val) => handleSearchParams({ name: 'filter', value: val })}
          dataTestId='opportunity-list-filter-select'
        />
        <div className='sort-reset'>
          <SelectDropdown
            name='sortBy'
            id='sortById'
            formId=''
            list={projectSort}
            placeholder={'Sort'}
            value={searchParams.sortBy}
            handleChange={(val) => handleSearchParams({ name: 'sortBy', value: val })}
            dataTestId='opportunity-list-sort-select'
          />
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>
      <Flex classes={['w-100']} variant='column'>
        <div className='input-label mb-8' data-testid='opportunity-list-toggle-label'>
          {' '}
          {isCloseStatusToggle ? 'Active Opportunities' : 'Closed Opportunities'}{' '}
        </div>
        <Toggle
          checked={isCloseStatusToggle}
          handleChange={handleClosingFilterToggle}
          dataTestId='opportunity-list-toggle'
        />
      </Flex>
      <Line />
      {opportunities && opportunities.length > 0 ? (
        <>
          <div className='w-100'>
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='opportunities-table-container'
            >
              <table className={isCloseStatusToggle ? 'styled-table' : 'no-styled-table'}>
                <thead>
                  <tr>
                    <th>Opportunity Name</th>
                    <th>Asset Class</th>
                    <th>Target Return</th>
                    <th>Minimum Amount</th>
                    <th>Committed %</th>
                    <th>Closing Status</th>
                    <th>Publish Status</th>
                    <th>Order</th>
                    <th>Investments</th>
                    <th>Redemptions</th>
                    <th>Edit</th>
                    {/* <th>Delete</th> */}
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map(
                    ({
                      id,
                      opportunity_title,
                      opportunity_slug,
                      investment_return,
                      asset_class,
                      closing_date,
                      currency_code,
                      ticket_size,
                      raise_goal,
                      total_investment_amount,
                      status,
                      asset_class_data,
                      display_order,
                    }: Opportunity) => (
                      <OpportunityCard
                        key={id}
                        id={id}
                        opportunitySlug={opportunity_slug}
                        closingDate={closing_date}
                        opportunityTitle={opportunity_title}
                        status={capitalizeStatus(status)}
                        committedPercentage={
                          total_investment_amount
                            ? calculateCommittedPercentage(
                                total_investment_amount.toString(),
                                raise_goal?.toString(),
                                asset_class,
                                asset_class_data,
                              )
                            : '0%'
                        }
                        expectedReturn={displayPercentage(investment_return?.toString() ?? '0')}
                        assetClass={asset_class}
                        ticketSize={displayTicketSize(ticket_size?.toString(), currency_code)}
                        displayOrder={display_order}
                        canEditOrder={canEditOrder}
                        handleDisplayOrderSave={handleDisplayOrderSave}
                        handleClick={handleProjectClick}
                        handleProjectInvestmentClick={handleProjectInvestmentClick}
                        handleProjectWithdrawalClick={handleProjectWithdrawalClick}
                      />
                    ),
                  )}
                </tbody>
              </table>
            </Flex>
          </div>
          {breakpoint && breakpoint === SM ? null : (
            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          )}
        </>
      ) : (
        <Flex variant={'column-center'} classes={['mt-12']}>
          <Span classes={['center']} variant={'thin'} text='No Opportunities Found' />
        </Flex>
      )}
    </Flex>
  )
}

export { OpportunityListPresenter }
